﻿import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from '@tanstack/react-router';
import { Badge, type BadgeVariant } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { Textarea } from '@heyroot/ui/primitives/Textarea';
import { ChevronLeft } from 'lucide-react';
import { fetchEscalationDetail, submitReview, queryKeys } from '../lib/api';
import { useAdminAuth } from '../lib/auth-context';
import { PERMS } from '../../rbac/permissions';

interface Props {
  escalationId: string;
}

function tierVariant(tier: string | null): BadgeVariant {
  if (tier === 'high') return 'success';
  if (tier === 'medium') return 'warning';
  if (tier === 'low') return 'danger';
  return 'neutral';
}

function verdictLabel(v: string) {
  if (v === 'approved') return 'Correct escalation';
  if (v === 'edited') return "Should've been resolved";
  if (v === 'rejected') return 'Operator training needed';
  return v;
}

const VERDICT_OPTIONS = [
  { value: 'approved', label: 'Correct escalation' },
  { value: 'edited', label: "Should've been resolved" },
  { value: 'rejected', label: 'Operator training needed' },
] as const;

export function AssistantEscalationDetailPage({ escalationId }: Props) {
  const navigate = useNavigate();
  const { hasPerm } = useAdminAuth();
  const qc = useQueryClient();
  const [verdict, setVerdict] = useState<'approved' | 'edited' | 'rejected'>('approved');
  const [notes, setNotes] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data, isPending, isError, error } = useQuery({
    queryKey: queryKeys.escalation(escalationId),
    queryFn: () => fetchEscalationDetail(escalationId),
  });

  const mutation = useMutation({
    mutationFn: () => submitReview(escalationId, verdict, notes.trim() || null),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.escalation(escalationId) });
      setNotes('');
      setSubmitError(null);
    },
    onError: (err) => {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit review.');
    },
  });

  if (isPending) {
    return <div className="text-body-sm text-muted animate-pulse">Loading…</div>;
  }
  if (isError || !data) {
    return (
      <div
        role="alert"
        className="rounded-control border border-danger bg-danger-pale text-danger-text text-body-sm p-4"
      >
        {error instanceof Error ? error.message : 'Failed to load escalation.'}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      {/* Back nav */}
      {/* eslint-disable-next-line @heyroot/heyroot-ui/no-one-off-ui -- plain back-nav button, no design variant needed */}
      <button
        type="button"
        onClick={() => void navigate({ to: '/assistant' })}
        className="inline-flex items-center gap-1 text-body-sm text-muted hover:text-primary transition-colors bg-transparent border-none cursor-pointer p-0 self-start"
      >
        <ChevronLeft size={14} />
        Back to inbox
      </button>

      {/* Escalation header */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <h1 className="text-heading-2 text-primary">Escalation</h1>
          <Badge variant={data.status === 'open' ? 'warning' : 'neutral'}>{data.status}</Badge>
          {data.confidenceTier && (
            <Badge variant={tierVariant(data.confidenceTier)}>
              {data.confidenceTier} confidence
            </Badge>
          )}
        </div>
        {hasPerm(PERMS.ASSISTANT_RESOLVE) && (
          <Link
            to="/assistant/$escalationId/chat"
            params={{ escalationId }}
            className="text-body-sm-emphasis text-primary underline-offset-2 hover:underline"
          >
            Live Chat →
          </Link>
        )}
        <p className="text-caption text-muted">
          {new Date(data.createdAt).toLocaleString()} · tenant {data.tenantId.slice(0, 8)}…
        </p>
      </div>

      {/* Q&A */}
      <section className="flex flex-col gap-3">
        <div className="rounded-control border border-border bg-surface p-4">
          <p className="text-caption text-muted mb-1">Operator question</p>
          <p className="text-body-sm text-primary">{data.question}</p>
        </div>
        {data.assistantResponse && (
          <div className="rounded-control border border-border bg-surface-sunken p-4">
            <p className="text-caption text-muted mb-1">Assistant response</p>
            <p className="text-body-sm text-secondary whitespace-pre-wrap">
              {data.assistantResponse}
            </p>
          </div>
        )}
        {data.escalationReason && (
          <div className="rounded-control border border-warning-subtle bg-warning-pale p-3">
            <p className="text-caption text-muted mb-1">Escalation reason</p>
            <p className="text-body-sm text-primary">{data.escalationReason}</p>
          </div>
        )}
      </section>

      {/* Annotation form */}
      <section className="flex flex-col gap-3">
        <h2 className="text-heading-3 text-primary">Submit annotation</h2>
        <fieldset className="flex flex-col gap-2 border-none p-0 m-0">
          <legend className="text-caption text-muted mb-1">Verdict</legend>
          <div className="flex flex-wrap gap-2">
            {VERDICT_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`flex items-center gap-2 cursor-pointer rounded-control border px-3 py-1.5 transition-colors ${
                  verdict === opt.value
                    ? 'border-brand-primary bg-brand-primary-pale text-brand-primary text-body-sm-emphasis'
                    : 'border-input bg-surface text-body-sm text-secondary hover:border-primary'
                }`}
              >
                <input
                  type="radio"
                  name="verdict"
                  value={opt.value}
                  checked={verdict === opt.value}
                  onChange={() => setVerdict(opt.value)}
                  className="sr-only"
                />
                {opt.label}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="flex flex-col gap-1">
          <label htmlFor="review-notes" className="text-caption text-muted">
            Notes (optional)
          </label>
          <Textarea
            id="review-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            maxLength={10000}
            placeholder="Add context, corrections, or training notes…"
          />
        </div>
        {submitError && (
          <p role="alert" className="text-body-sm text-danger-text">
            {submitError}
          </p>
        )}
        <div>
          <Button variant="primary" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? 'Submitting…' : 'Submit annotation'}
          </Button>
        </div>
      </section>

      {/* Review history */}
      {data.reviews.length > 0 && (
        <section className="flex flex-col gap-2">
          <h2 className="text-heading-3 text-primary">Review history</h2>
          <ul className="flex flex-col gap-2 list-none p-0 m-0">
            {data.reviews.map((rev) => (
              <li
                key={rev.id}
                className="rounded-control border border-border bg-surface p-3 flex flex-col gap-1"
              >
                <div className="flex items-center gap-2">
                  <span className="text-body-sm-emphasis leading-4 text-primary">
                    {verdictLabel(rev.verdict)}
                  </span>
                  <span className="text-caption text-muted">
                    · {new Date(rev.createdAt).toLocaleString()}
                  </span>
                </div>
                {rev.notes && <p className="text-body-sm text-secondary">{rev.notes}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
